// Use this hook only in useDbOrgEditActions. Elsewhere, get the action from
// useOrgEditActions() so the active OrgContext implementation applies.
//
// Writes go through the backend, never straight to Hasura: since GOUV-15 the
// right to edit a role depends on the governance mode, on who holds the role,
// and on which fields are being written — none of which a Hasura permission can
// express (one update permission per table and per role, one column list, one
// filter). Keeping a direct path for the cases Hasura does allow is what broke
// on 08/08/2026: the screen opened a field the server then refused, in silence.
// See packages/backend/src/features/role/updateRole.ts.
import useCreateLog from '@/log/hooks/useCreateLog'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { RoleFragment } from '@gql'
import { getEntityChanges } from '@rolebase/shared/helpers/log/getEntityChanges'
import { EntityChangeType, LogType } from '@rolebase/shared/model/log'
import { useCallback } from 'react'
import { trpc } from 'src/trpc'

export default function useUpdateRole() {
  const createLog = useCreateLog()
  // The org comes from the context, never from the role passed in: callers hand
  // over a RoleSummaryFragment cast as a RoleFragment (RoleEditModal does), and
  // a summary carries no orgId — reading it there silently sent `undefined` and
  // every rename was rejected (08/08/2026).
  const { orgId } = useOrgContext()

  return useCallback(
    async (
      role: RoleFragment,
      values: Partial<RoleFragment>,
      options?: { meetingId?: string }
    ) => {
      if (!orgId) throw new Error('No org')
      await trpc.role.update.mutate({
        orgId,
        roleId: role.id,
        values,
        // Present only for a meeting step's fields (REU04-D14). The server
        // re-checks everything it implies; sending it never suffices on its own.
        // It comes from the caller, never from a context read here: this hook
        // lives at the org level, above any meeting step (#170).
        meetingId: options?.meetingId,
      })

      // Log change (diff prev/new)
      const { prevData, newData } = getEntityChanges(role, values)
      createLog({
        display: {
          type: LogType.RoleUpdate,
          id: role.id,
          name: role.name,
        },
        changes: {
          roles: [
            {
              type: EntityChangeType.Update,
              id: role.id,
              prevData,
              newData,
            },
          ],
        },
      })
    },
    [createLog, orgId]
  )
}
